import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Sparkles, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EmailComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTo?: string;
  defaultSubject?: string;
}

export function EmailComposeDialog({ open, onOpenChange, defaultTo = "", defaultSubject = "" }: EmailComposeDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [sending, setSending] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  
  const [composeForm, setComposeForm] = useState({
    to: defaultTo,
    subject: defaultSubject,
    body: "",
  });
  const [aiPrompt, setAiPrompt] = useState("");

  useEffect(() => {
    if (open) {
      checkGmailConnection();
      setComposeForm({
        to: defaultTo,
        subject: defaultSubject,
        body: "",
      });
    }
  }, [open, defaultTo, defaultSubject]);

  const checkGmailConnection = async () => {
    setCheckingConnection(true);
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'status' }
      });

      if (error) throw error;

      if (data?.connected && data?.access_token) {
        setAccessToken(data.access_token);
        setIsConnected(true);
      } else {
        setIsConnected(false);
      }
    } catch (error: any) {
      console.error('Connection check failed:', error);
      setIsConnected(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  const generateContent = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Enter a prompt",
        description: "Please describe what you want to write",
        variant: "destructive",
      });
      return;
    }

    setGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-email-content', {
        body: {
          description: aiPrompt,
          recipient: composeForm.to || 'the recipient',
        }
      });

      if (error) throw error;

      const content = data?.body || data?.content || data?.email || '';
      if (content) {
        setComposeForm(prev => ({
          ...prev,
          body: content,
          subject: prev.subject || data?.subject || prev.subject,
        }));
        setAiPrompt("");
        toast({
          title: "Content generated",
          description: "AI has drafted your email",
        });
      }
    } catch (error: any) {
      toast({
        title: "Generation failed",
        description: error.message || "Could not generate content",
        variant: "destructive",
      });
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleSendEmail = async () => {
    if (!composeForm.to || !composeForm.subject || !composeForm.body) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (!isConnected) {
      toast({
        title: "Not authenticated",
        description: "Please connect Gmail first",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      // Send via tracked edge function
      const { data, error } = await supabase.functions.invoke('gmail-send-tracked', {
        body: {
          to: composeForm.to,
          subject: composeForm.subject,
          body: composeForm.body,
          enableTracking: true,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Email sent",
        description: `Email sent successfully to ${composeForm.to}`,
      });

      onOpenChange(false);
      setComposeForm({ to: "", subject: "", body: "" });
    } catch (error: any) {
      toast({
        title: "Error sending email",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  if (checkingConnection) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!isConnected) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Connect Gmail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">
              To send emails from the Sales Hub, you need to connect your Gmail account first.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => {
                onOpenChange(false);
                navigate('/email');
              }} className="w-full">
                <Mail className="h-4 w-4 mr-2" />
                Connect in Email Hub
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Compose Email</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">To</label>
            <Input
              placeholder="recipient@example.com"
              value={composeForm.to}
              onChange={(e) => setComposeForm(prev => ({ ...prev, to: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Subject</label>
            <Input
              placeholder="Email subject"
              value={composeForm.subject}
              onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">AI Assist</label>
            <div className="flex gap-2">
              <Input
                placeholder="Describe what you want to write..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !generatingContent && generateContent()}
              />
              <Button 
                onClick={generateContent} 
                disabled={generatingContent || !aiPrompt.trim()}
                size="sm"
              >
                {generatingContent ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Message</label>
            <Textarea
              placeholder="Write your email here..."
              value={composeForm.body}
              onChange={(e) => setComposeForm(prev => ({ ...prev, body: e.target.value }))}
              rows={10}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
